import sortBy from 'lodash/sortBy'
import { getValue, isPending } from 'mobx-async'
import { observer } from 'mobx-react'
import React, { FC } from 'react'

import { RiskLevel } from '@avantstay/arriere-clients/dist/arriereBackoffice'
import {
  capitalizeEachFirstLetter,
  CheckboxBasedFilter,
  mainColors,
  riskChannelsColors,
} from '@avantstay/backoffice-core'

// eslint-disable-next-line import/no-cycle
import { BookingsTapeChartStore } from '@new/domains/bookingsTapeChart/stores/BookingsTapeChartStore'

import { FiltersSharedProps } from './Filters.types'

export const colorByRiskStatus = {
  [RiskLevel.low]: riskChannelsColors.riskLow,
  [RiskLevel.medium]: riskChannelsColors.riskMedium,
  [RiskLevel.high]: riskChannelsColors.riskHigh,
  [RiskLevel.veryHigh]: riskChannelsColors.riskVeryHigh,
  [RiskLevel.undefined]: mainColors.neutralDark,
}

export const hierarchyByRiskLevel = {
  [RiskLevel.veryHigh]: 1,
  [RiskLevel.high]: 2,
  [RiskLevel.medium]: 3,
  [RiskLevel.low]: 4,
}

interface RiskLevelFilterProps extends FiltersSharedProps {
  initialValue?: string[]
  title?: string
  shouldShowCounter?: boolean
  onChange: (value?: string[]) => void
}

const _RiskLevelFilter: FC<RiskLevelFilterProps> = ({ shouldShowCounter = true, ...props }) => {
  const { tapeChartCounters } = BookingsTapeChartStore

  const isLoadingCounter = isPending(tapeChartCounters)
  const riskLevelFilterCounter = isLoadingCounter ? null : getValue(tapeChartCounters)?.riskLevel

  const renderCounter = (riskLevel: RiskLevel) => (
    <span style={{ marginLeft: 'auto' }}>
      {riskLevelFilterCounter?.find(({ name }) => name === riskLevel)?.value ?? 0}
    </span>
  )

  const riskLevelToRemove = ['undefined', 'veryLow', 'minimal']

  const riskLevelOptions = Object.keys(RiskLevel)
    .filter(riskLevelKey => !riskLevelToRemove.includes(riskLevelKey))
    .map(riskLevelKey => {
      const dotKey = riskLevelKey.replace(/[A-Z]/g, letter => `_${letter}`).toUpperCase()
      const value = RiskLevel[riskLevelKey as keyof typeof RiskLevel]

      return {
        value,
        hasFullWidth: true,
        label: (
          <>
            {capitalizeEachFirstLetter(value).replace(/_/g, ' ')}

            {shouldShowCounter && !isLoadingCounter && renderCounter(value)}
          </>
        ),
        dot: colorByRiskStatus[dotKey as keyof typeof colorByRiskStatus],
        hierarchy: hierarchyByRiskLevel[dotKey as keyof typeof hierarchyByRiskLevel],
      }
    })

  const sortedOptions = sortBy(riskLevelOptions, 'hierarchy')

  return <CheckboxBasedFilter options={sortedOptions} {...props} />
}

export const RiskLevelFilter = observer(_RiskLevelFilter)
